import React, { useRef, useState, useCallback } from 'react';

// This component expects an array of SLUs with importance and satisfaction values
const SLUPlot = ({ slus, onSLUMoved }) => {
  const plotSize = 800; // Size of the plot in pixels
  const svgRef = useRef(null);

  const [dragging, setDragging] = useState(false);
  const [selectedSLU, setSelectedSLU] = useState(null);

  const toSvgCoordinates = (value) => (value / 10) * plotSize;
  const toValueCoordinates = (svgValue) => (svgValue / plotSize) * 10;

  const handleMouseDown = (slu, event) => {
    setDragging(true);
    setSelectedSLU(slu);
  };

  const handleMouseMove = useCallback(
    (event) => {
      if (dragging && selectedSLU) {
        const svgRect = svgRef.current.getBoundingClientRect();
        const svgX = event.clientX - svgRect.left;
        const svgY = event.clientY - svgRect.top;
        
        // Calculate the new values based on the mouse position
        const newImportance = toValueCoordinates(plotSize - svgY);
        const newSatisfaction = toValueCoordinates(svgX);

        onSLUMoved(selectedSLU.name, newImportance, newSatisfaction);
      }
    },
    [dragging, selectedSLU, onSLUMoved, plotSize]
  );

  const handleMouseUp = () => {
    setDragging(false);
    setSelectedSLU(null);
  };

  return (
    <svg
      ref={svgRef}
      width={plotSize}
      height={plotSize}
      style={{ border: '1px solid black' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseUp}
      onMouseUp={handleMouseUp}
    >
      {slus.map((slu, index) => (
        <React.Fragment key={index}>
          <circle
            cx={toSvgCoordinates(slu.satisfaction)}
            cy={plotSize - toSvgCoordinates(slu.importance)} // SVG 0 is at top, so we invert
            r="5" // radius of the circle
            fill="red"
            onMouseDown={(event) => handleMouseDown(slu, event)}
            style={{ cursor: dragging ? 'grabbing' : 'pointer' }}
          />
          <text
            x={toSvgCoordinates(slu.satisfaction) + 7} // Offset text to the right of the circle
            y={plotSize - toSvgCoordinates(slu.importance)} // Align text vertically with circle
            fontSize="10"
            alignmentBaseline="middle"
          >
            {slu.name}
          </text>
        </React.Fragment>
      ))}
      {/* Add axes */}
      <line x1="0" y1={plotSize / 2} x2={plotSize} y2={plotSize / 2} stroke="black" />
      <line x1={plotSize / 2} y1="0" x2={plotSize / 2} y2={plotSize} stroke="black" />
      {/* Vertical Axis Label (Importance) */}
      <text
        x={plotSize / 2 - 10}
        y={10}
        transform={`rotate(-90 ${plotSize / 2 - 10} 10)`} // Rotate the text for vertical axis
        fontSize="16"
        alignmentBaseline="middle"
        textAnchor="end"
      >
        Importance
      </text>

      {/* Horizontal Axis Label (Satisfaction) */}
      <text
        x={plotSize - 10} // Position towards the end of the horizontal axis
        y={plotSize / 2 - 5} // Position a little above the bottom of the plot
        fontSize="16"
        textAnchor="end"
      >
        Satisfaction
      </text>
    </svg>
  );
};

export default SLUPlot;
